import {
  memo,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";

function getClampSplit(
  width: number,
  fullText: string,
  lineCount: number,
  styleSource: CSSStyleDeclaration,
): { visible: string; overflow: string } {
  if (!fullText || width <= 0) {
    return { visible: fullText, overflow: "" };
  }

  const single = lineCount === 1;
  const measurer = document.createElement(single ? "span" : "div");
  measurer.style.cssText = single
    ? "position:absolute;visibility:hidden;pointer-events:none;white-space:nowrap;left:-9999px;top:0"
    : `position:absolute;visibility:hidden;pointer-events:none;left:-9999px;top:0;width:${width}px;overflow:hidden;display:-webkit-box;-webkit-line-clamp:${lineCount};-webkit-box-orient:vertical`;
  measurer.style.font = styleSource.font;
  measurer.style.fontSize = styleSource.fontSize;
  measurer.style.fontWeight = styleSource.fontWeight;
  measurer.style.letterSpacing = styleSource.letterSpacing;
  if (!single) {
    measurer.style.whiteSpace = styleSource.whiteSpace;
    measurer.style.lineHeight = styleSource.lineHeight;
    measurer.style.wordBreak = styleSource.wordBreak;
  }
  document.body.appendChild(measurer);

  measurer.textContent = fullText;
  const fits = single
    ? measurer.offsetWidth <= width + 1
    : measurer.scrollHeight <= measurer.clientHeight + 1;
  if (fits) {
    document.body.removeChild(measurer);
    return { visible: fullText, overflow: "" };
  }

  let low = 0;
  let high = fullText.length;
  let visibleCount = 0;
  while (low <= high) {
    const mid = (low + high) >> 1;
    measurer.textContent = fullText.slice(0, mid);
    const ok = single
      ? measurer.offsetWidth <= width
      : measurer.scrollHeight <= measurer.clientHeight + 1;
    if (ok) {
      visibleCount = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  // Leave room for an inline "…" so the trigger isn't clipped.
  if (visibleCount > 0) {
    const withDots = () => {
      measurer.textContent = `${fullText.slice(0, visibleCount)}…`;
    };
    withDots();
    while (
      visibleCount > 0 &&
      (single
        ? measurer.offsetWidth > width
        : measurer.scrollHeight > measurer.clientHeight + 1)
    ) {
      visibleCount -= 1;
      withDots();
    }
  }

  document.body.removeChild(measurer);

  const visible = fullText.slice(0, visibleCount).trimEnd();
  const overflow = fullText.slice(visible.length).trimStart();
  if (!overflow) {
    return { visible: fullText, overflow: "" };
  }
  return { visible, overflow };
}

export const ClampedTextWithTooltip = memo(function ClampedTextWithTooltip({
  id,
  text,
  className,
  style,
  lines = 2,
}: {
  id: string;
  text: string;
  className?: string;
  style?: CSSProperties;
  lines?: number;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleText, setVisibleText] = useState(text);
  const [overflowText, setOverflowText] = useState("");
  const [tooltipWidth, setTooltipWidth] = useState<number | null>(null);

  const measureOverflow = useCallback(() => {
    const el = containerRef.current;
    if (!el || !text.trim()) {
      setVisibleText(text);
      setOverflowText("");
      setTooltipWidth(null);
      return;
    }

    const width = el.clientWidth;
    setTooltipWidth(el.closest("article")?.clientWidth ?? width);
    const computed = window.getComputedStyle(el);
    const { visible, overflow } = getClampSplit(width, text, lines, computed);
    setVisibleText(visible);
    setOverflowText(overflow);
  }, [text, lines]);

  useLayoutEffect(() => {
    measureOverflow();
    const el = containerRef.current;
    if (!el) return;
    const card = el.closest("article");
    const observer = new ResizeObserver(measureOverflow);
    observer.observe(el);
    if (card) observer.observe(card);
    return () => observer.disconnect();
  }, [measureOverflow]);

  const single = lines === 1;

  return (
    <div
      ref={containerRef}
      className={`min-w-0 ${className ?? ""}`}
      style={{
        maxWidth: "100%",
        overflow: "hidden",
        ...(single
          ? { whiteSpace: "nowrap", textOverflow: "clip" }
          : { whiteSpace: "pre-line" }),
        ...style,
      }}
    >
      {overflowText ? (
        <>
          {visibleText}
          <OverlayTrigger
            placement="top"
            overlay={
              <Popover
                id={id}
                className="border shadow-sm"
                style={
                  tooltipWidth
                    ? { width: tooltipWidth, maxWidth: tooltipWidth }
                    : undefined
                }
              >
                <Popover.Body className="small text-start text-dark py-2 px-3 text-wrap">
                  {overflowText}
                </Popover.Body>
              </Popover>
            }
          >
            <button
              type="button"
              className="btn p-0 border-0 bg-transparent fw-bold lh-1 align-baseline"
              style={{
                color: "inherit",
                letterSpacing: "0.06em",
                fontSize: "1em",
                verticalAlign: "baseline",
                marginLeft: "0.1em",
              }}
              aria-label="Show more"
            >
              …
            </button>
          </OverlayTrigger>
        </>
      ) : (
        text
      )}
    </div>
  );
});
